import { useEffect, useRef } from 'react';
import { GridFilterModel } from '@mui/x-data-grid';
import { SavedFilterType } from 'shared';

import { useCreateSavedFilterMutation } from '../services/api/savedFiltersApi/savedFilters';
import { MUI_DATAGRID_MAJOR_VERSION } from '../lib/datagrid';

// localStorage blob shape written by the old usePersistedPresets hook
interface LegacyFilterPreset {
  id: string;
  name: string;
  filterModel: GridFilterModel;
  createdAt: string;
}

interface LegacyFilterPresetStore {
  presets: LegacyFilterPreset[];
  activePresetId: string | null;
}

const LEGACY_PRESET_KEYS: Array<{ storageKey: string; type: SavedFilterType }> = [
  { storageKey: `agedContactReportFilterPresets`, type: SavedFilterType.ReportAged },
  { storageKey: `lateFeeReportFilterPresets`, type: SavedFilterType.ReportLateFee },
  { storageKey: `emailReportFilterPresets`, type: SavedFilterType.ReportEmail },
];

// One-time migration of the reports' old browser-local filter presets to the
// user-scoped saved-filters API. Name collisions (e.g. already imported from
// another tab) are skipped; a key is only cleared once its presets have all
// been processed, so a network failure retries on the next visit.
export function useImportLocalStoragePresets() {
  const [createSavedFilter] = useCreateSavedFilterMutation();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    async function importKey(storageKey: string, type: SavedFilterType) {
      let store: LegacyFilterPresetStore | null = null;

      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        store = JSON.parse(raw) as LegacyFilterPresetStore;
      }
      catch {
        return;
      }

      if (!Array.isArray(store?.presets)) {
        localStorage.removeItem(storageKey);

        return;
      }

      for (const preset of store.presets) {
        if (!preset?.name || !preset.filterModel) continue;

        try {
          await createSavedFilter({
            name: preset.name,
            type,
            gridState: { filter: { filterModel: preset.filterModel } },
            gridVersion: MUI_DATAGRID_MAJOR_VERSION,
          }).unwrap();
        }
        catch (err) {
          // Name collision or bad preset: skip it. Anything network-shaped
          // aborts so the key survives for a retry on the next visit.
          const status = (err as { status?: unknown })?.status;
          if (status === undefined || status === `FETCH_ERROR` || status === `TIMEOUT_ERROR`) {
            return;
          }
        }
      }

      localStorage.removeItem(storageKey);
    }

    for (const { storageKey, type } of LEGACY_PRESET_KEYS) {
      importKey(storageKey, type);
    }
  }, [createSavedFilter]);
}
